/**
 * ListLoading — the shared "still fetching" indicator for list screens.
 *
 * 2026-05-21 (Anil) — owner feedback (item 9): the Clients / Projects /
 * Partners lists showed the empty state ("no clients yet") DURING the
 * Firestore fetch, then populated. That is wrong — an empty state must
 * appear ONLY when the collection is genuinely empty.
 *
 * The fix is a three-state model every list screen now follows:
 *   loading  → <ListLoading />             (first snapshot not yet in)
 *   empty    → <EmptyNote title="No …" />  (fetch done, zero rows)
 *   rows     → the list
 *
 * The list screens get `loading` from the `listenProjects` /
 * `listenPartners` subscription, which now reports the first snapshot
 * (success OR error) via an `onReady` callback. Until that fires this
 * skeleton shows, so the empty state can never flash during a fetch.
 */

/** Skeleton rows — calm placeholder while the first snapshot is in flight. */
export function ListLoading({
  label = 'Loading…',
  rows = 4,
}: {
  label?: string;
  rows?: number;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          aria-hidden
          className="card row-card skel-row"
          style={{ animationDelay: `${i * 90}ms` }}
        >
          <div className="skel-line" style={{ width: '38%' }} />
          <div
            className="skel-line"
            style={{ width: '18%', marginLeft: 'auto' }}
          />
        </div>
      ))}
    </div>
  );
}
